import type { useAddressFormatter } from '@wix/yoshi-flow-editor';

import type { Address } from '../types/businessTypes';

type RawAddress = {
  [K in keyof Address]?: Address[K] | null;
};

const toCommonGeocode = (
  raw: RawAddress['geocode'],
): Address['geocode'] | undefined => {
  const latitude = raw?.latitude ?? undefined;
  const longitude = raw?.longitude ?? undefined;

  if (latitude == null && longitude == null) {
    return undefined;
  }

  return { latitude, longitude };
};

/**
 * Normalizes any ambassador address-shaped object into our {@link Address}:
 * replaces `null` fields with `undefined` and drops empty geocodes.
 */
export const toCommonAddress = (raw: unknown): Address | undefined => {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }

  const source = raw as RawAddress;

  return {
    addressLine: source.addressLine ?? undefined,
    addressLine2: source.addressLine2 ?? undefined,
    city: source.city ?? undefined,
    country: source.country ?? undefined,
    countryFullname: source.countryFullname ?? undefined,
    formattedAddress: source.formattedAddress ?? undefined,
    geocode: toCommonGeocode(source.geocode),
    hint: source.hint ?? undefined,
    postalCode: source.postalCode ?? undefined,
    streetAddress: source.streetAddress ?? undefined,
    subdivision: source.subdivision ?? undefined,
    subdivisionFullname: source.subdivisionFullname ?? undefined,
    subdivisions: source.subdivisions ?? undefined,
  };
};

/**
 * An address is renderable when it has a country and at least one geocode
 * coordinate. Coordinates are checked for presence (`null` / `undefined`) —
 * `0` is a valid latitude / longitude (equator, prime meridian).
 */
export const isAddressValid = (address?: Address): address is Address =>
  !!address?.country &&
  (address.geocode?.latitude != null || address.geocode?.longitude != null);

/**
 * Renders an {@link Address} into a single comma-joined string.
 *
 * Tries the platform `formatAddress` first; falls back through the raw fields
 * (`formattedAddress`, `addressLine`, `streetAddress.formattedAddressLine`)
 * when the formatter throws or returns nothing. Returns `''` for missing or
 * fully-empty addresses — callers rely on this to drive the
 * "Add delivery address" CTA.
 */
export const formatAddressString = (
  address: Address | undefined,
  formatAddress: ReturnType<typeof useAddressFormatter>,
): string => {
  if (!address) {
    return '';
  }

  let formatted = '';

  try {
    formatted = formatAddress({ address }, { appendCountry: false })
      .filter((part) => part && /\S/.test(part))
      .join(', ');
  } catch {
    // formatAddress can throw on partial addresses (no country / geocode);
    // fall through to the raw-field chain below.
  }

  return (
    formatted ||
    address.formattedAddress ||
    address.addressLine ||
    address.streetAddress?.formattedAddressLine ||
    ''
  );
};

// Argentine CPA, e.g. "C1428-BNF": province letter, 4 digits, 3 block letters.
const HYPHENATED_ARGENTINE_CPA = /\b([A-Z]\d{4})-([A-Z]{3})\b/gi;

/**
 * Removes the hyphen from Argentine CPA postal codes (`C1428-BNF` → `C1428BNF`)
 * in free-text autocomplete input. Google Places returns no predictions for
 * some inputs that repeat the hyphenated form, while the unhyphenated form
 * (the way Google itself formats CPAs) matches.
 */
export const normalizeArgentineCpa = (input: string): string =>
  input.replace(HYPHENATED_ARGENTINE_CPA, '$1$2');
